import { motion } from 'framer-motion';
import React from 'react';

import avatar from '../../assets/images/Muser.jpg';

type LearningOptionPopup = {
  imageUrl: string;
  name: string;
  role: string;
  confidence: number;
  wageUplift: string;
  salaryTarget: string;
  requiredSkills?: string[];
  quality: string[];
  summary: string;
};

export const LearningOptionPopup: React.FC<LearningOptionPopup> = ({
  imageUrl,
  name,
  role,
  confidence,
  wageUplift,
  salaryTarget,
  quality,
  summary,
}) => {
  return (
    <motion.div
      className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      {/* Header */}
      <motion.div
        className="flex flex-col md:flex-row justify-between items-center gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {/* Profile */}
        <div className="flex items-center gap-4">
          <motion.img
            src={imageUrl || avatar}
            alt={avatar}
            className="w-16 h-16 rounded-full object-cover"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 120 }}
          />
          <div>
            <h2 className="text-xl font-bold text-gray-900">{name}</h2>
            <p className="text-sm text-gray-800 font-semibold">{role ? `${role}` : 'Student'}</p>
          </div>
        </div>

        {/* Confidence Circle */}
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="relative w-20 h-20">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="40" cy="40" r="36" stroke="#E5E7EB" strokeWidth="8" fill="transparent" />
              <motion.circle
                cx="40"
                cy="40"
                r="36"
                stroke="#1D4ED8"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={2 * Math.PI * 36}
                strokeDashoffset={2 * Math.PI * 36 * (1 - confidence / 100)}
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: confidence / 100 }}
                transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.3 }}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center font-bold text-lg">
              {confidence}%
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">Match</p>
        </motion.div>
      </motion.div>

      <hr className="my-6" />

      {/* Wage uplift + Salary target */}
      <motion.div
        className="flex flex-col md:flex-row justify-between items-center text-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <p className="text-green-800 font-medium">
          <span className="text-gray-600 font-medium">Wage Sparkline </span>
          {wageUplift}
        </p>
        <p className="font-bold text-gray-900">{salaryTarget}</p>
      </motion.div>

      <hr className="my-6" />

      {/* Strengths */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.4 }}
      >
        <h3 className="font-semibold text-gray-900 mb-3">Your Leading Strengths</h3>
        <div className="flex flex-wrap gap-2 text-sm text-gray-700">
          {quality.length > 0 &&
            quality.map((item, idx) => {
              const formattedItem = item
                .replace(/_/g, ' ') // Replace underscores with spaces
                .toLowerCase() // Convert all letters to lowercase first
                .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word

              return (
                <motion.div
                  key={idx}
                  className="bg-green-100 text-green-700 px-4 py-1 rounded-full text-sm font-medium flex items-center gap-2"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * idx, duration: 0.3 }}
                  whileHover={{ scale: 1.06 }}
                >
                  ✔ {formattedItem}
                </motion.div>
              );
            })}
        </div>
      </motion.div>

      <hr className="my-6" />

      {/* Summary */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.4 }}
      >
        <h3 className="font-semibold text-gray-900 mb-3">Why this profile fits for you?</h3>
        {summary && (
          <motion.div
            className="flex items-center gap-2 text-gray-700"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <span>✔ {summary}</span>
          </motion.div>
        )}
      </motion.div>

      {/* Required Skills */}
      {/* <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      >
        <h3 className="font-semibold text-gray-900 mb-3">Required Skills</h3>
        <div className="flex flex-wrap gap-2">
          {requiredSkills.map((skill, idx) => (
            <motion.span
              key={idx}
              className="bg-indigo-50 text-indigo-700 px-4 py-1 rounded-full text-sm font-medium"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * idx, duration: 0.2 }}
              whileHover={{ scale: 1.06 }}
            >
              {skill}
            </motion.span>
          ))}
        </div>
      </motion.div> */}
    </motion.div>
  );
};
